import { type FC, type ReactNode } from "react";

import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { BaseButton } from "../BaseButton";

export type FilterOption = {
  label: string;
  value: string | number;
};

export type FilterSection = {
  title: string;
  component: ReactNode;
};

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  sections: FilterSection[];
  onClear?: () => void;
}

export const FilterDrawer: FC<FilterDrawerProps> = ({ open, onClose, title, sections, onClear }) => {
  const { t } = useTranslation();

  return (
    <StyledDrawer anchor="right" open={open} onClose={onClose}>
      <DrawerContent>
        <DrawerHeader>
          <Typography variant="h3">{title}</Typography>
          <IconButton onClick={onClose} aria-label="Close filter drawer" size="small">
            <FontAwesomeIcon icon={faXmark} size="sm" />
          </IconButton>
        </DrawerHeader>

        <FilterSections spacing={2}>
          {sections.map(section => (
            <Stack spacing={2} key={section.title}>
              <Box>{section.component}</Box>
            </Stack>
          ))}
        </FilterSections>

        <DrawerFooter>
          <BaseButton variant="secondary" onClick={onClear} text={t("wiki.clear_filters")} />
          <BaseButton variant="primary" onClick={onClose} text={t("library.show_results")} />
        </DrawerFooter>
      </DrawerContent>
    </StyledDrawer>
  );
};

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    width: 400px;
    max-width: 90vw;
  }
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const FilterSections = styled(Stack)`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const DrawerFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 24px 16px;
  background: ${props => props.theme.colors.white};
`;
